import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { firestoreService } from '@/services/firestore'
import { UserRole } from '@types'

export const useAuthStore = defineStore('auth', () => {
  const { currentUser, isAuthenticated, isLoading, mfaRequired, login, verifyMfaCode, cancelMfa, logout } =
    useAuth()
  const userRole = ref<UserRole | null>(null)
  const organizationId = ref<string | null>(null)
  const allowedTags = ref<string[]>([])
  const roleLoading = ref(false)

  const userEmail = computed(() => currentUser.value?.email || '')
  const userId = computed(() => currentUser.value?.uid || '')
  // Only super_admin (and the legacy 'admin' alias) is globally unscoped -
  // sees every tag, no allowedTags check. org_admin (GitHub #24) is scoped by
  // allowedTags exactly like a plain user; it's distinguished only by
  // canManageUsers below, not by data visibility.
  const isSuperAdmin = computed(() => userRole.value === ('admin' as UserRole) || userRole.value === ('super_admin' as UserRole))
  const isOrgAdmin = computed(() => userRole.value === ('org_admin' as UserRole))
  const canManageUsers = computed(() => isSuperAdmin.value || isOrgAdmin.value)

  /**
   * Gates the super-admin console (tag cycling, platform-wide accounts, audit
   * trail). Deliberately stricter than isSuperAdmin above: the legacy 'admin'
   * alias sees every tag's data, but only a real 'super_admin' passes
   * assertSuperAdmin() in functions/src/admin/guard.ts and isSuperAdmin() in
   * firestore.rules - offering the console to anyone else would just show
   * them a page of permission errors. Like the route guard that uses it, this
   * only decides what the UI offers; every console call is authorised again
   * server-side.
   */
  const canUseAdminConsole = computed(() => userRole.value === ('super_admin' as UserRole))

  // Fetch user role, organization, and allowed tags from Firestore
  async function fetchUserRole() {
    if (!isAuthenticated.value) {
      userRole.value = null
      organizationId.value = null
      allowedTags.value = []
      return
    }

    try {
      roleLoading.value = true
      const user = await firestoreService.getUser(currentUser.value!.uid)
      userRole.value = user?.role || null
      organizationId.value = user?.organizationId || null
      allowedTags.value = user?.allowedTags || []
    } catch (error) {
      console.error('Failed to fetch user role:', error)
      userRole.value = 'user' // Default to user role on error
      organizationId.value = null
      allowedTags.value = []
    } finally {
      roleLoading.value = false
    }
  }

  // Override logout to clear role, organizationId, and allowedTags
  async function logoutWithRole() {
    await logout()
    userRole.value = null
    organizationId.value = null
    allowedTags.value = []
  }

  // fetchUserRole() must actually run for isAdmin/allowedTags to ever be
  // populated - watching currentUser here means it fires automatically on
  // login (and on refresh, once the session restores) rather than relying
  // on every consumer remembering to call it themselves.
  watch(
    currentUser,
    user => {
      if (user) {
        fetchUserRole()
      } else {
        userRole.value = null
        organizationId.value = null
        allowedTags.value = []
      }
    },
    { immediate: true }
  )

  return {
    currentUser,
    isAuthenticated,
    isLoading,
    mfaRequired,
    userEmail,
    userId,
    userRole,
    organizationId,
    allowedTags,
    isSuperAdmin,
    isOrgAdmin,
    canManageUsers,
    canUseAdminConsole,
    roleLoading,
    login,
    verifyMfaCode,
    cancelMfa,
    logout: logoutWithRole,
    fetchUserRole,
  }
})
